/**
 * QuickHarvestModal Component
 *
 * POS-style modal for rapid harvest weight entry.
 * Optimised for numpad input with large display, continuous entry mode,
 * and minimal fields (batch selector + weight only).
 *
 * Features:
 * - Large numpad display for weight entry
 * - Continuous entry mode ("Record + Another")
 * - Session tracking (running total across entries)
 * - Both on-screen and physical keyboard input
 * - Today's harvests panel for at-a-glance tracking
 *
 * Part of Quick Harvest Recording feature
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  SelectGroup,
  SelectLabel
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useHarvestMutations } from '@/hooks/useHarvest'
import { useHarvestBatches } from '@/hooks/useHarvestBatches'
import { useAllStrains } from '@/hooks/useStrains'
import { useTodaysHarvests } from '@/hooks/useTodaysHarvests'
import { Zap, Clock, Leaf, AlertCircle, Plus, Scale, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import WeightNumpad from './WeightNumpad'
import { parseDateString, toLocalDateString } from '@/utils/dateUtils'

// Special value for creating new legacy batch
const CREATE_NEW_LEGACY = '__CREATE_NEW__'

export default function QuickHarvestModal({ isOpen, onClose, onSave }) {
  // Form state
  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [selectedStrainId, setSelectedStrainId] = useState('')
  const [batchCode, setBatchCode] = useState('')
  const [weightString, setWeightString] = useState('')
  const [error, setError] = useState(null)

  // Session state (persists across entries in continuous mode)
  const [sessionTotal, setSessionTotal] = useState(0)
  const [sessionCount, setSessionCount] = useState(0)

  // Hooks
  const {
    legacyBatches,
    standardBatches,
    loading: batchesLoading,
    refetch: refetchBatches
  } = useHarvestBatches()

  const { strains, loading: strainsLoading } = useAllStrains({ activeOnly: true })
  const { harvestsByStrain, totalKg: todaysTotalKg, loading: harvestsLoading, refetch: refetchHarvests } = useTodaysHarvests()

  const { recordLegacyHarvest, loading: submitting } = useHarvestMutations()

  // Computed - is creating new legacy batch?
  const isCreatingNewLegacy = selectedBatchId === CREATE_NEW_LEGACY

  // Get selected batch object
  const selectedBatch = useMemo(() => {
    if (!selectedBatchId || selectedBatchId === CREATE_NEW_LEGACY) return null
    return [...(legacyBatches || []), ...(standardBatches || [])].find(
      b => b.id === selectedBatchId
    )
  }, [selectedBatchId, legacyBatches, standardBatches])

  // Check if selected batch is a legacy batch
  const isLegacyBatch = useMemo(() => {
    if (isCreatingNewLegacy) return true
    return selectedBatch?.is_legacy || false
  }, [selectedBatch, isCreatingNewLegacy])

  // Check if form is valid
  const isValid = useMemo(() => {
    // For new legacy, need strain and weight
    if (isCreatingNewLegacy) {
      if (!selectedStrainId) return false
    } else {
      // For existing batch, need batch selected
      if (!selectedBatchId) return false
    }
    if (!weightString) return false
    const weight = parseFloat(weightString)
    if (isNaN(weight) || weight <= 0 || weight > 50) return false
    return true
  }, [selectedBatchId, selectedStrainId, weightString, isCreatingNewLegacy])

  // Reset form when modal opens (but preserve session stats)
  useEffect(() => {
    if (isOpen) {
      setWeightString('')
      setSelectedStrainId('')
      setBatchCode('')
      setError(null)
      // Don't reset batch selection - remember last used
      refetchHarvests()
    }
  }, [isOpen, refetchHarvests])

  // Reset session when modal closes
  const handleClose = useCallback(() => {
    setSessionTotal(0)
    setSessionCount(0)
    setSelectedBatchId('')
    setSelectedStrainId('')
    setBatchCode('')
    setWeightString('')
    setError(null)
    onClose()
  }, [onClose])

  // Handle form submission
  const handleSubmit = useCallback(async (keepOpen = false) => {
    if (!isValid || submitting) return

    setError(null)
    const weight = parseFloat(weightString)

    // Check if this is a standard batch - not supported in quick mode
    if (!isLegacyBatch) {
      setError('Standard batches require container selection. Use the full Record Harvest form.')
      return
    }

    try {
      // Get strain ID - either from selected strain (new legacy) or from selected batch
      const strainId = isCreatingNewLegacy
        ? selectedStrainId
        : selectedBatch?.strain?.id

      const strainName = isCreatingNewLegacy
        ? strains.find(s => s.id === selectedStrainId)?.name || 'Unknown'
        : selectedBatch?.strain?.name || selectedBatch?.strain?.code || 'Unknown'

      const params = {
        strain_id: strainId,
        harvest_date: toLocalDateString(new Date()),
        weight_kg: weight,
        quality_grade: 'A', // Default for quick mode
      }

      // Pass batch_code if user entered one (for new legacy batches)
      if (isCreatingNewLegacy && batchCode.trim()) {
        params.batch_code = batchCode.trim()
      }

      await recordLegacyHarvest(params)

      // Update session stats
      setSessionTotal(prev => prev + weight)
      setSessionCount(prev => prev + 1)

      // Show success toast
      toast.success(`Recorded ${weight.toFixed(2)} kg`, {
        description: strainName
      })

      if (keepOpen) {
        // Reset for next entry, but keep batch selected
        setWeightString('')
        // If creating new legacy, keep the strain selected too
        // Refetch batches and harvests to update totals
        refetchBatches()
        refetchHarvests()
      } else {
        // Trigger parent callback and close
        onSave?.()
        handleClose()
      }
    } catch (err) {
      console.error('[QuickHarvestModal] Submit error:', err)
      setError(err.message || 'Failed to record harvest')
    }
  }, [isValid, submitting, weightString, selectedBatch, selectedStrainId, batchCode, strains, isLegacyBatch, isCreatingNewLegacy, recordLegacyHarvest, refetchBatches, refetchHarvests, onSave, handleClose])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isValid && !submitting) {
        e.preventDefault()
        // Enter = Record + Another (continuous entry), Shift+Enter = Record and close
        handleSubmit(!e.shiftKey)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isValid, submitting, handleSubmit])

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Harvest
            </DialogTitle>
            {sessionCount > 0 && (
              <div className="text-sm text-content-secondary">
                Session:{' '}
                <span className="font-semibold text-green-600">
                  {sessionTotal.toFixed(2)} kg
                </span>
                <span className="text-content-tertiary ml-1">
                  ({sessionCount})
                </span>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-2">
          {/* Left column: Batch selector + Numpad */}
          <div className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Batch Selector */}
            <div className="space-y-2">
              <Label htmlFor="quick-batch">Batch</Label>
              <Select
                value={selectedBatchId}
                onValueChange={(value) => {
                  setSelectedBatchId(value)
                  setError(null)
                }}
                disabled={batchesLoading || submitting}
              >
                <SelectTrigger id="quick-batch">
                  <SelectValue placeholder={batchesLoading ? 'Loading...' : 'Select batch...'} />
                </SelectTrigger>
                <SelectContent>
                  {/* Create New Legacy Option - at top */}
                  <SelectItem value={CREATE_NEW_LEGACY}>
                    <div className="flex items-center gap-2 text-odin-blue">
                      <Plus className="h-4 w-4" />
                      <span>Create New Legacy Batch</span>
                    </div>
                  </SelectItem>

                  {/* Separator after create new */}
                  {(legacyBatches?.length > 0 || standardBatches?.length > 0) && (
                    <SelectSeparator />
                  )}

                  {/* Legacy Batches */}
                  {legacyBatches?.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        Legacy Batches
                      </SelectLabel>
                      {legacyBatches.map(batch => (
                        <SelectItem key={batch.id} value={batch.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{batch.batch_code}</span>
                            <Badge variant="secondary" className="text-xs">
                              {batch.strain?.code}
                            </Badge>
                            <span className="text-xs text-content-tertiary">
                              {formatDate(batch.inoculation_date)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}

                  {/* Separator if both sections have items */}
                  {legacyBatches?.length > 0 && standardBatches?.length > 0 && (
                    <SelectSeparator />
                  )}

                  {/* Standard Batches - enabled but will show error on submit */}
                  {standardBatches?.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2">
                        <Leaf className="h-3 w-3" />
                        Standard Batches
                      </SelectLabel>
                      {standardBatches.map(batch => (
                        <SelectItem key={batch.id} value={batch.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{batch.batch_code}</span>
                            <Badge variant="outline" className="text-xs">
                              {batch.strain?.code}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {batch.current_state?.replace('_', ' ')}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>

              {/* Selected batch info */}
              {selectedBatch && (
                <div className="flex items-center gap-2 text-sm text-content-secondary">
                  <Badge variant="outline" className="text-xs">
                    {selectedBatch.strain?.name || selectedBatch.strain?.code}
                  </Badge>
                  {selectedBatch.total_harvest_kg > 0 && (
                    <span className="text-xs">
                      Total: {selectedBatch.total_harvest_kg.toFixed(2)} kg
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Strain & Batch Code - only shown when creating new legacy */}
            {isCreatingNewLegacy && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="quick-strain">Strain</Label>
                  <Select
                    value={selectedStrainId}
                    onValueChange={setSelectedStrainId}
                    disabled={strainsLoading || submitting}
                  >
                    <SelectTrigger id="quick-strain">
                      <SelectValue placeholder={strainsLoading ? 'Loading...' : 'Select strain...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {strains.map(strain => (
                        <SelectItem key={strain.id} value={strain.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{strain.code}</span>
                            <span>{strain.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quick-batch-code">Batch Code</Label>
                  <Input
                    id="quick-batch-code"
                    value={batchCode}
                    onChange={(e) => setBatchCode(e.target.value)}
                    placeholder="e.g. 260210LM (auto if blank)"
                    disabled={submitting}
                    className="font-mono"
                  />
                  <p className="text-xs text-content-tertiary">
                    Enter existing code to record against it, or new code to create it. Leave blank to auto-generate.
                  </p>
                </div>
              </>
            )}

            {/* Weight Numpad */}
            <WeightNumpad
              value={weightString}
              onChange={setWeightString}
              disabled={submitting || (!selectedBatchId) || (isCreatingNewLegacy && !selectedStrainId)}
              autoFocus={true}
              maxValue={50}
            />
          </div>

          {/* Right column: Today's Harvests Panel */}
          <div className="border-l border-border pl-6 hidden sm:block">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Today's Harvests
              </h3>
              {!harvestsLoading && todaysTotalKg > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {todaysTotalKg.toFixed(2)} kg
                </Badge>
              )}
            </div>

            {harvestsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-content-tertiary" />
              </div>
            ) : Object.keys(harvestsByStrain).length === 0 ? (
              <div className="text-center py-8 text-content-tertiary text-sm">
                No harvests recorded today
              </div>
            ) : (
              <ScrollArea className="h-[280px]">
                <div className="space-y-4 pr-4">
                  {Object.entries(harvestsByStrain).map(([strainName, data]) => (
                    <div key={strainName} className="space-y-1">
                      {/* Strain header with total */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{strainName}</span>
                        <span className="text-sm text-odin-green font-semibold">
                          {data.total.toFixed(2)} kg
                        </span>
                      </div>
                      {/* Individual harvests */}
                      <div className="space-y-0.5">
                        {data.harvests.map((harvest) => (
                          <div
                            key={harvest.id}
                            className="flex items-center justify-between text-xs text-content-tertiary pl-2"
                          >
                            <span className="flex items-center gap-2">
                              <span>{harvest.time}</span>
                              <span className="font-mono">{harvest.batch_code}</span>
                            </span>
                            <span>{harvest.weight_kg.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            className="sm:order-1"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            disabled={!isValid || submitting}
            className="sm:order-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Record + Another
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            disabled={!isValid || submitting}
            className="sm:order-3"
          >
            {submitting ? 'Recording...' : 'Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
